/**
 * Simple Shopify Service
 * 
 * Uses Shopify's public JSON endpoints - no app setup required!
 * Works with any Shopify store's public product data
 */

// Use native fetch instead of axios to avoid adapter issues

interface SimpleShopifyProduct {
  id: number
  title: string
  body_html: string
  vendor: string
  product_type: string
  created_at: string
  updated_at: string
  published_at: string | null
  tags: string[]
  variants: Array<{
    id: number
    title: string
    option1: string | null
    option2: string | null
    option3: string | null
    sku: string
    requires_shipping: boolean
    taxable: boolean
    featured_image: any
    available: boolean
    price: string
    grams: number
    compare_at_price: string | null
    position: number
    product_id: number
    created_at: string
    updated_at: string
  }>
  images: Array<{
    id: number
    created_at: string
    position: number
    updated_at: string
    product_id: number
    src: string
    variant_ids: number[]
    width: number
    height: number
    alt: string | null
  }>
  options: Array<{
    name: string
    position: number
    values: string[]
  }>
  available: boolean
  handle: string
}

interface ShopifyProductsResponse {
  products: SimpleShopifyProduct[]
}

class SimpleShopifyService {
  private storeUrl: string

  constructor() {
    this.storeUrl = import.meta.env.VITE_SHOPIFY_STORE_URL || 'the-salty-mermaid-9081.myshopify.com'
    
    // Remove protocol if present
    this.storeUrl = this.storeUrl.replace(/^https?:\/\//, '')
    
    console.log('SimpleShopifyService: Initialized for store:', this.storeUrl)
  }

  /**
   * Check if service is configured
   */
  isConfigured(): boolean {
    return !!this.storeUrl
  }

  /**
   * Get status for admin display
   */
  getStatus() {
    return {
      configured: this.isConfigured(),
      storeUrl: this.storeUrl,
      hasCredentials: true, // No credentials needed for public API
      connected: this.isConfigured(),
      enabled: true
    }
  }

  /**
   * Test connection by fetching a single product
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      if (!this.isConfigured()) {
        return { success: false, message: 'Store URL not configured' }
      }

      // Test with products.json endpoint (limited to first product)
      const response = await fetch(`https://${this.storeUrl}/products.json?limit=1`)
      
      if (response.ok) {
        const data = await response.json()
        if (data && data.products) {
          return {
            success: true,
            message: `Connected to ${this.storeUrl} - Found ${data.products.length > 0 ? 'products' : 'store (no products)'}`
          }
        } else {
          return { success: false, message: 'Invalid response from store' }
        }
      } else {
        return { success: false, message: `HTTP ${response.status}: ${response.statusText}` }
      }
    } catch (error: any) {
      console.error('SimpleShopifyService: Connection test failed:', error)
      return {
        success: false,
        message: `Connection failed: ${error.message}`
      }
    }
  }

  /**
   * Fetch products from Shopify's public JSON endpoint
   */
  async getProducts(limit: number = 250): Promise<SimpleShopifyProduct[]> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Store URL not configured')
      }

      console.log('SimpleShopifyService: Fetching products from:', this.storeUrl)
      
      const allProducts: SimpleShopifyProduct[] = []
      let page = 1
      const maxPages = Math.ceil(limit / 250) // Maximum products per page is 250
      
      // Keep fetching until we have all products or reach the limit
      while (allProducts.length < limit && page <= maxPages) {
        const pageLimit = Math.min(250, limit - allProducts.length)
        const url = `https://${this.storeUrl}/products.json?limit=${pageLimit}&page=${page}`
        
        console.log(`SimpleShopifyService: Fetching page ${page} (${pageLimit} products)...`)
        
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        const data: ShopifyProductsResponse = await response.json()
        const products = data.products || []
        
        if (products.length === 0) {
          // No more products to fetch
          break
        }
        
        allProducts.push(...products)
        page++
        
        // If we got fewer products than requested, we've reached the end
        if (products.length < pageLimit) {
          break
        }
      }

      console.log(`SimpleShopifyService: Retrieved ${allProducts.length} total products`)
      return allProducts
    } catch (error: any) {
      console.error('SimpleShopifyService: Error fetching products:', error)
      throw new Error(`Failed to fetch products: ${error.message}`)
    }
  }

  /**
   * Get a specific product by handle
   */
  async getProductByHandle(handle: string): Promise<SimpleShopifyProduct> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Store URL not configured')
      }

      const response = await fetch(`https://${this.storeUrl}/products/${handle}.json`)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      const data = await response.json()
      return data.product
    } catch (error: any) {
      console.error('SimpleShopifyService: Error fetching product:', error)
      throw new Error(`Failed to fetch product: ${error.message}`)
    }
  }

  /**
   * Convert Shopify product to Gallery item format
   */
  convertToGalleryItem(shopifyProduct: SimpleShopifyProduct) {
    const firstVariant = shopifyProduct.variants[0]
    const price = firstVariant ? parseFloat(firstVariant.price) : 0
    
    // Better artist attribution - avoid generic "Printify"
    const getArtistName = (vendor: string, title: string) => {
      if (!vendor || vendor === 'Printify' || vendor === 'Default Title') {
        // Try to extract artist from title or use store name
        return 'Salty Mermaid Art'
      }
      return vendor
    }
    
    return {
      id: `shopify_${shopifyProduct.id}`,
      title: shopifyProduct.title,
      artist: getArtistName(shopifyProduct.vendor, shopifyProduct.title),
      description: this.stripHtml(shopifyProduct.body_html || ''),
      price: price,
      category: this.mapProductTypeToCategory(shopifyProduct.product_type),
      medium: shopifyProduct.product_type || 'Mixed Media',
      dimensions: '', // Shopify doesn't have standard dimensions field
      imageId: shopifyProduct.images[0]?.id.toString() || undefined,
      inStock: shopifyProduct.available,
      forSale: true, // All Shopify products are for sale
      isShopifyProduct: true,
      tags: Array.isArray(shopifyProduct.tags) ? shopifyProduct.tags : 
             (typeof shopifyProduct.tags === 'string' ? shopifyProduct.tags.split(',').map(tag => tag.trim()) : []),
      isActive: shopifyProduct.published_at !== null,
      shopifyData: {
        id: shopifyProduct.id.toString(),
        handle: shopifyProduct.handle,
        updatedAt: shopifyProduct.updated_at,
        images: shopifyProduct.images
      }
    }
  }

  /**
   * Generate Shopify product URL for "Buy on Shopify" button
   */
  getProductUrl(handle: string): string {
    return `https://${this.storeUrl}/products/${handle}`
  }

  /**
   * Strip HTML tags from product descriptions
   */
  private stripHtml(html: string): string {
    if (!html) return ''
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent || ''
  }

  /**
   * Map Shopify product types to gallery categories
   * Use the original product type from Shopify for better categorization
   */
  private mapProductTypeToCategory(productType: string): string {
    // Return the product type as-is, or default to 'Uncategorized'
    return productType && productType.trim() ? productType.trim() : 'Uncategorized'
  }
}

// Create singleton instance
export const simpleShopifyService = new SimpleShopifyService()

export { SimpleShopifyService }